/**
 * Make a file URL actually download.
 *
 * The `download` attribute on an anchor is **silently ignored for cross-origin
 * URLs**. That did not matter while the CV was `/resume.pdf`, a file bundled
 * into the site's own origin — the attribute worked and the button saved the
 * file. The moment the CV is uploaded through the admin it lives on Supabase
 * Storage, which is a different origin, and the same button would quietly stop
 * downloading and start opening a PDF viewer tab instead.
 *
 * Nothing would look broken, which is the problem: a recruiter clicks Résumé,
 * gets a viewer, and has to work out how to save it.
 *
 * Supabase Storage answers that with a `download` query parameter, which makes
 * it send `Content-Disposition: attachment`. The rewrite is deliberately narrow
 * — only URLs that are recognisably Supabase public objects are touched, so a
 * CV hosted anywhere else is passed through untouched rather than having a
 * parameter invented for it.
 */

import { mediaUrl } from "./media";

/** Supabase's public object path. Matching on it keeps the rewrite honest. */
const SUPABASE_PUBLIC_OBJECT = "/storage/v1/object/public/";

export function downloadable(url: string, filename = "resume.pdf"): string {
  const resolved = mediaUrl(url);
  if (!resolved.includes(SUPABASE_PUBLIC_OBJECT)) return resolved;
  // The stored object name is a random UUID, so naming the download explicitly
  // is the difference between saving "resume.pdf" and saving "9f2c…a1.pdf".
  // Files served by our own API already carry a Content-Disposition filename,
  // so they need nothing added.
  const separator = resolved.includes("?") ? "&" : "?";
  return `${resolved}${separator}download=${encodeURIComponent(filename)}`;
}
